import { useState } from "react";
import { Plus, ShoppingBag, Briefcase, Calendar, User, Search, MessageCircle, MapPin, Navigation } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BottomNavigation from "@/components/BottomNavigation";
import FunctionTile from "@/components/FunctionTile";
import { useUser } from "@/contexts/UserContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import OnboardingFlow from "@/components/OnboardingFlow";

const Index = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();

  // Check if user is new (in real app, this would come from user context/API)
  const isNewUser = !localStorage.getItem('onboarding_completed');

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    switch (tab) {
      case 'home':
        navigate('/');
        break;
      case 'marketplace':
        navigate('/marketplace');
        break;
      case 'berichten':
        navigate('/berichten');
        break;
      case 'vacatures':
        navigate('/vacatures');
        break;
      case 'profiel':
        navigate('/profiel');
        break;
    }
  };
  const mainFunctions = [
    {
      title: "WOM Plaza",
      description: "Ontdek diensten en producten van vrouwen in België",
      icon: Search,
      onClick: () => {
        console.log("Navigating to WOM Plaza");
        navigate('/marketplace');
      }
    },
    {
      title: "Berichten",
      description: "Chat met andere vrouwen over diensten en projecten",
      icon: MessageCircle,
      onClick: () => {
        console.log("Navigating to Messages");
        navigate('/berichten');
      }
    },
    {
      title: "Dienst Plaatsen",
      description: "Deel je diensten met vrouwen in België",
      icon: Plus,
      onClick: () => {
        console.log("Navigating to Post Service form");
        navigate('/dienst-plaatsen');
      }
    },
    {
      title: "Product Toevoegen",
      description: "Plaats je producten te koop in België",
      icon: ShoppingBag,
      onClick: () => {
        console.log("Navigating to Add Product form");
        navigate('/product-toevoegen');
      }
    },
    {
      title: "Vacatures Bekijken",
      description: "Ontdek kansen bij bedrijven in België",
      icon: Briefcase,
      onClick: () => {
        console.log("Navigating to Vacancies list");
        navigate('/vacatures');
      }
    },
    {
      title: "Afspraken Beheren",
      description: "Plan en bekijk je afspraken en boekingen",
      icon: Calendar,
      onClick: () => {
        console.log("Navigating to Appointments");
        navigate('/afspraken');
      }
    },
    {
      title: "Mijn Profiel",
      description: "Bekijk en bewerk je profielinformatie",
      icon: User,
      onClick: () => {
        console.log("Navigating to Profile");
        navigate('/profiel');
      }
    }
  ];

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f6f1eb' // Perfect beige-100
    }}>
      {/* Header Section */}
      <div className="px-6 pt-8 pb-4">
        <div className="max-w-2xl mx-auto">
          {/* Welcome Header */}
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#efe7dd', border: '1px solid #e6dbd0' }}>
            <div className="text-center">
              <div className="mb-2">
                <h1 className="text-3xl font-light tracking-wider" style={{ color: '#837069' }}>
                  <span className="font-bold" style={{ color: '#a0897c' }}>W</span>
                  <span className="mx-1" style={{ color: '#d9cbbf' }}>·</span>
                  <span className="font-bold" style={{ color: '#a0897c' }}>O</span>
                  <span className="mx-1" style={{ color: '#d9cbbf' }}>·</span>
                  <span className="font-bold" style={{ color: '#a0897c' }}>M</span>
                </h1>
              </div>
              <p className="text-sm" style={{ color: '#b6a092' }}>
                Voor vrouwen die hun dromen waarmaken en samen groeien
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="px-6 pb-32">
        {/* Welcome Section */}
        <div className="max-w-2xl mx-auto text-center mb-12">          
          {/* Hero Section - Clean Professional */}
          <div style={{
            backgroundColor: '#fbf8f4', // Perfect beige-50
            borderColor: '#efe7dd', // Perfect beige-200
            border: '1px solid',
            borderRadius: '8px',
            padding: '32px',
            marginBottom: '32px',
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)'
          }}>
            <div className="mb-6">
              <h2 className="text-4xl md:text-5xl font-light mb-2 tracking-wider" style={{ color: '#837069' }}>
                <span className="font-bold" style={{ color: '#a0897c' }}>Woman on a Mission</span>
              </h2>

              <p className="text-lg font-normal leading-relaxed mb-6" style={{ color: '#b6a092' }}>
                Connect, groei en realiseer je dromen<br />
                samen met ambitieuze vrouwen in België
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {isNewUser && (
                  <Button
                    onClick={() => setShowOnboarding(true)}
                    size="lg"
                    className="hover:opacity-90"
                    style={{ backgroundColor: '#a0897c', color: 'white' }}
                  >
                    Start je WOM Journey
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/marketplace')}
                  size="lg"
                  className="hover:opacity-90"
                  style={{ backgroundColor: '#a0897c', color: 'white' }}
                >
                  Ontdek WOM Plaza
                </Button>
              </div>
            </div>
          </div>

          <div style={{
            backgroundColor: '#efe7dd', // Perfect beige-200
            borderColor: '#e6dbd0', // Perfect beige-300
            border: '1px solid',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <p className="font-medium text-lg mb-2" style={{ color: '#837069' }}>
              Welkom bij jouw community
            </p>
            <p className="text-sm" style={{ color: '#b6a092' }}>
              Meer dan 10.000 vrouwen hebben al hun mission gevonden
            </p>
          </div>
        </div>

        {/* Function Cards */}
        <div className="max-w-2xl mx-auto space-y-4">
          {mainFunctions.map((func, index) => (
            <FunctionTile
              key={index}
              title={func.title}
              description={func.description}
              icon={func.icon}
              onClick={func.onClick}
              className="w-full"
            />
          ))}
        </div>

        {/* Quick Access Cards */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="grid grid-cols-2 gap-4">
            {/* Map View Quick Access */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-1"
              style={{ backgroundColor: '#fbf8f4', borderColor: '#efe7dd' }}
              onClick={() => navigate('/marketplace')}
            >
              <CardContent className="p-4 text-center">
                <div 
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: '#efe7dd' }}
                >
                  <MapPin className="h-6 w-6" style={{ color: '#a0897c' }} />
                </div>
                <h3 className="font-medium text-sm mb-1" style={{ color: '#837069' }}>
                  Diensten Kaart
                </h3>
                <p className="text-xs" style={{ color: '#b6a092' }}>
                  Ontdek lokale diensten
                </p>
                <Badge className="mt-2 text-xs" style={{ 
                  backgroundColor: '#e6dbd0', 
                  color: '#a0897c' 
                }}>
                  België
                </Badge>
              </CardContent>
            </Card>

            {/* Navigation Quick Access */}
            <Card 
              className="cursor-pointer hover:shadow-md transition-all hover:-translate-y-1"
              style={{ backgroundColor: '#fbf8f4', borderColor: '#efe7dd' }}
              onClick={() => navigate('/marketplace')}
            >
              <CardContent className="p-4 text-center">
                <div 
                  className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                  style={{ backgroundColor: '#efe7dd' }}
                >
                  <Navigation className="h-6 w-6" style={{ color: '#a0897c' }} />
                </div>
                <h3 className="font-medium text-sm mb-1" style={{ color: '#837069' }}>
                  Route Planner
                </h3>
                <p className="text-xs" style={{ color: '#b6a092' }}>
                  Navigeer naar diensten
                </p>
                <Badge className="mt-2 text-xs" style={{ 
                  backgroundColor: '#e6dbd0', 
                  color: '#a0897c' 
                }}>
                  Nieuw
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Location Status Banner */}
          <div 
            className="mt-4 p-3 rounded-lg text-center"
            style={{ backgroundColor: '#efe7dd', borderColor: '#e6dbd0', border: '1px solid' }}
          >
            <div className="flex items-center justify-center gap-2 mb-1">
              <MapPin className="h-4 w-4" style={{ color: '#b6a092' }} />
              <span className="text-sm font-medium" style={{ color: '#837069' }}>
                Jouw locatie: België
              </span>
            </div>
            <p className="text-xs" style={{ color: '#b6a092' }}>
              Ontdek vrouwen en diensten bij jou in de buurt
            </p>
          </div>
        </div>
      </div>

      {/* Onboarding Flow */}
      <OnboardingFlow
        isOpen={showOnboarding}
        onClose={() => {
          setShowOnboarding(false);
          localStorage.setItem('onboarding_completed', 'true');
        }}
      />
      {/* Bottom Navigation */}
      <BottomNavigation activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
};

export default Index;